import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { createContext, useContext } from 'react';
import { EnrollmentPeriod } from 'types/enrollmentService';
import { Group } from 'types/groupService';
import { INITIAL_GROUP_OBJECT } from 'store/domain/groupStore';
import { getAllEnrollmentPeriodsByGroupId } from 'api/operations/enrollmentService';
import { getGroupById } from 'api/operations/groupService';
import { getActiveEPeriods, getNewSaleEPeriods, getQuotingPeriods } from 'global/services-helpers/enrollmentService';

export default class ZywaveGroupInfoPageStore {
  private _isLoading = false;
  private _activeEnrollmentPeriods: EnrollmentPeriod[] = [];
  private _group: Group = INITIAL_GROUP_OBJECT;

  public get isLoading(): boolean {
    return this._isLoading;
  }

  /**
   * New sale enrollments with confirmed proposals
   */
  public get activeNewSaleEnrollmentPeriods(): EnrollmentPeriod[] {
    const activePeriods = getActiveEPeriods(this._activeEnrollmentPeriods.map((period) => toJS(period)));
    return getNewSaleEPeriods(activePeriods);
  }

  /**
   * Quoting enrollments
   */
  public get quotingEnrollmentPeriods(): EnrollmentPeriod[] {
    return getQuotingPeriods(this._activeEnrollmentPeriods);
  }

  public get groupName(): string {
    return this._group.name;
  }

  public get groupAORNpn(): string {
    return this._group.agentOfRecord?.npn || 'Not specified';
  }

  private setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public async getInitialData(groupId: string): Promise<void> {
    this.setIsLoading(true);

    const promisesResult = await Promise.all([getAllEnrollmentPeriodsByGroupId(groupId), getGroupById(groupId)]);

    runInAction(() => {
      this._activeEnrollmentPeriods = promisesResult[0];
      this._group = promisesResult[1];

      this.setIsLoading(false);
    });
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const ZywaveGroupInfoPageStoreContext = createContext(new ZywaveGroupInfoPageStore());

export const useZywaveGroupInfoPageStore = (): ZywaveGroupInfoPageStore => {
  return useContext(ZywaveGroupInfoPageStoreContext);
};

export { ZywaveGroupInfoPageStoreContext };
